import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { ExternalLink } from 'lucide-react'
import { userApi } from '../api/userApi'
import type { User } from '@/types/entities'
import { UserRole } from '@/types/entities'

const profileFormSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters').max(15),
  lastName: z.string().min(2, 'Last name must be at least 2 characters').max(15),
  artisticName: z.string().min(2).max(30).optional().or(z.literal('')),
  bio: z.string().min(20, 'Bio must be at least 20 characters').optional().or(z.literal('')),
  paypal: z.string().email('Must be a valid email address').optional().or(z.literal('')),
})

type ProfileFormData = z.infer<typeof profileFormSchema>

interface ProfileFormProps {
  user: User
  onSubmit: (data: Partial<User>) => void
  isSubmitting?: boolean
}

export function ProfileForm({ user, onSubmit, isSubmitting = false }: ProfileFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      artisticName: user.artisticName || '',
      bio: user.bio || '',
      paypal: user.paypal || '',
    },
  })

  const isArtist = user.role === UserRole.ARTIST
  const shopifyStoreUrl = userApi.getShopifyStoreUrl(user.shopifyCollectionHandle)

  const handleFormSubmit = (data: ProfileFormData) => {
    onSubmit(data)
  }

  return (
    <Card className="shadow-subtle">
      <CardHeader>
        <CardTitle className="text-2xl">Profile Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="firstName"
                {...register('firstName')}
                className={errors.firstName ? 'border-destructive' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lastName"
                {...register('lastName')}
                className={errors.lastName ? 'border-destructive' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user.email} disabled className="bg-muted" />
            <p className="text-sm text-muted-foreground">Email cannot be changed</p>
          </div>

          {isArtist && (
            <>
              <div className="space-y-2">
                <Label htmlFor="artisticName">Artist Name</Label>
                <Input
                  id="artisticName"
                  {...register('artisticName')}
                  disabled
                  className="bg-muted"
                />
                <p className="text-sm text-muted-foreground">
                  Artist name cannot be changed. Contact support if you need to update it.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Biography</Label>
                <Textarea
                  id="bio"
                  {...register('bio')}
                  rows={4}
                  className={errors.bio ? 'border-destructive' : ''}
                  placeholder="Tell us about your artistic journey, style, and inspiration..."
                />
                {errors.bio && <p className="text-sm text-destructive">{errors.bio.message}</p>}
                <p className="text-sm text-muted-foreground">
                  Share your story with potential buyers (minimum 20 characters)
                </p>
              </div>

              {shopifyStoreUrl && (
                <div className="space-y-2">
                  <Label>My Store URL</Label>
                  <a
                    href={shopifyStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    {shopifyStoreUrl}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="paypal">PayPal Email</Label>
                <Input
                  id="paypal"
                  type="email"
                  {...register('paypal')}
                  className={errors.paypal ? 'border-destructive' : ''}
                  placeholder="your-paypal@email.com"
                />
                {errors.paypal && (
                  <p className="text-sm text-destructive">{errors.paypal.message}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  We'll use this email to send your artwork sale payments
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3">
            <Button type="submit" disabled={!isDirty || isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

